import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Table, Input, Space, Select, DatePicker, Tag, Button, notification } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import {
    ItemBlockHistoryItem,
    PagedResult,
} from '../../types/itemblocktypes';
import { getItemBlockHistory } from '../../services/itemblockservice';
import { getDropdownUsers } from '../../services/userservice';
import { UserDropdownDto } from '../../types/userypes';
import { StoreDropdownDto } from '../../types/storetypes';
import { getAllStoresForDropdown } from '../../services/storeserivce';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface Props {
    activeTab: string;
}

type ItemType = 'Menu' | 'Recipe' | undefined;
type ActionType = 'Block' | 'Unblock' | undefined;

const ItemBlockHistoryTab: React.FC<Props> = ({ activeTab }) => {
    const [data, setData] = useState<ItemBlockHistoryItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ['5', '10', '20', '50', '100'],
        showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`,
    });

    // Filters
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [itemType, setItemType] = useState<ItemType>(undefined);
    const [action, setAction] = useState<ActionType>(undefined);
    const [storeNumber, setStoreNumber] = useState<string | undefined>(undefined);
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
    const [stores, setStores] = useState<StoreDropdownDto[]>([]);
    const [users, setUsers] = useState<UserDropdownDto[]>([]);
    const [userGuid, setUserGuid] = useState<string | undefined>(undefined);

    // --- Data fetcher (stable) ---
    const fetchHistory = useCallback(
        async (
            page: number,
            pageSize: number,
            itemName?: string,
            itemType?: 'Menu' | 'Recipe',
            action?: 'Block' | 'Unblock',
            storeNumber?: string,
            startDate?: string,
            endDate?: string,
            userGuid?: string
        ) => {
            setLoading(true);
            try {
                const response = await getItemBlockHistory(
                    page,
                    pageSize,
                    undefined, // menuGuid
                    undefined, // storeGuid
                    itemName,
                    storeNumber,
                    action, // maps to ?status=
                    itemType,
                    startDate,
                    endDate,
                    userGuid
                );
                const result: PagedResult<ItemBlockHistoryItem> = response.data;
                setData(result.items);

                // Avoid triggering effect loops by only updating pagination when values actually change
                setPagination(prev => {
                    const next = {
                        ...prev,
                        current: result.pageNumber,
                        pageSize: result.pageSize,
                        total: result.totalCount,
                    };
                    if (
                        prev.current === next.current &&
                        prev.pageSize === next.pageSize &&
                        prev.total === next.total
                    ) {
                        return prev; // no-op
                    }
                    return next;
                });
            } catch {
                notification.error({
                    message: 'Failed to load history',
                    description: 'An unexpected error occurred while fetching history.',
                });
            } finally {
                setLoading(false);
            }
        },
        []
    );

    // Debounced wrapper + cleanup
    const debouncedFetch = useMemo(() => debounce(fetchHistory, 300), [fetchHistory]);

    useEffect(() => {
        return () => {
            debouncedFetch.cancel();
        };
    }, [debouncedFetch]);

    // --- Bootstrap dropdowns on mount ---
    useEffect(() => {
        getDropdownUsers()
            .then(res => setUsers(res.data))
            .catch(() =>
                notification.error({
                    message: 'Failed to load users',
                    description: 'Could not fetch user list.',
                })
            );
    }, []);

    useEffect(() => {
        getAllStoresForDropdown()
            .then(res => setStores(res.data))
            .catch(() =>
                notification.error({
                    message: 'Failed to load stores',
                    description: 'Could not fetch store list.',
                })
            );
    }, []);

    // --- Single source of truth for fetching ---
    // Fires when active tab is "history" and relevant inputs change.
    useEffect(() => {
        if (activeTab !== 'history') return;

        debouncedFetch(
            pagination.current!,
            pagination.pageSize!,
            searchTerm,
            itemType,
            action,
            storeNumber,
            dateRange ? dateRange[0].toISOString() : undefined,
            dateRange ? dateRange[1].toISOString() : undefined,
            userGuid
        );
    }, [
        activeTab,
        searchTerm,
        itemType,
        action,
        storeNumber,
        dateRange,
        userGuid,
        pagination.current,
        pagination.pageSize,
        debouncedFetch,
    ]);

    // Table pagination change: update state only; the effect above will fetch.
    const handleTableChange = useCallback((newPagination: TablePaginationConfig) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));
    }, []);

    // Clear filters: update state only; the effect above will fetch.
    const clearFilters = useCallback(() => {
        setSearchTerm('');
        setItemType(undefined);
        setAction(undefined);
        setStoreNumber(undefined);
        setUserGuid(undefined);
        setDateRange(null);
        setPagination(prev => ({ ...prev, current: 1 }));
    }, []);

    const columns: ColumnsType<ItemBlockHistoryItem> = [
        {
            title: 'Date/Time',
            dataIndex: 'dateCreated',
            key: 'dateCreated',
            render: (val: string) => dayjs(val).format('DD MMM YYYY, HH:mm'),
        },
        { title: 'Item', dataIndex: 'itemName', key: 'itemName' },
        { title: 'Type', dataIndex: 'itemType', key: 'itemType' },
        { title: 'Store Number', dataIndex: 'storeNumber', key: 'storeNumber' },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            render: (val: 'Block' | 'Unblock') =>
                val === 'Block' ? <Tag color="red">{val}</Tag> : <Tag color="green">{val}</Tag>,
        },
        { title: 'Reason', dataIndex: 'reason', key: 'reason' },
        {
            title: 'Performed By',
            dataIndex: 'performedBy',
            key: 'performedBy',
            render: (val: string) => val || 'Unknown',
        },
    ];

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Space wrap>
                <Search
                    placeholder="Search by item name"
                    allowClear
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: 200 }}
                />
                <Select
                    placeholder="Filter by Type"
                    allowClear
                    value={itemType}
                    onChange={setItemType}
                    style={{ width: 150 }}
                >
                    <Option value="Menu">Menu</Option>
                    <Option value="Recipe">Recipe</Option>
                </Select>
                <Select
                    placeholder="Filter by Action"
                    allowClear
                    value={action}
                    onChange={setAction}
                    style={{ width: 150 }}
                >
                    <Option value="Block">Block</Option>
                    <Option value="Unblock">Unblock</Option>
                </Select>
                <Select
                    placeholder="Filter by Store Number"
                    allowClear
                    value={storeNumber}
                    onChange={setStoreNumber}
                    style={{ width: 200 }}
                    showSearch
                    optionFilterProp="children"
                >
                    {stores.map((s) => (
                        <Option key={s.storeGuid} value={s.storeNumber}>
                            {s.storeNumber}
                        </Option>
                    ))}
                </Select>
                <Select
                    placeholder="Filter by Performed By"
                    allowClear
                    value={userGuid}
                    onChange={setUserGuid}
                    style={{ width: 200 }}
                    showSearch
                    optionFilterProp="children"
                >
                    {users.map((u) => (
                        <Option key={u.guid} value={u.guid}>
                            {u.fullName}
                        </Option>
                    ))}
                </Select>
                <RangePicker
                    value={dateRange ?? undefined}
                    onChange={(range) => setDateRange(range as [dayjs.Dayjs, dayjs.Dayjs] | null)}
                    allowClear
                />
                <Button onClick={clearFilters}>Clear Filters</Button>
            </Space>

            <Table<ItemBlockHistoryItem>
                dataSource={data}
                columns={columns}
                pagination={pagination}
                loading={loading}
                rowKey="guid"
                onChange={handleTableChange}
            />
        </Space>
    );
};

export default ItemBlockHistoryTab;
